import { getAutoProps } from 'js/auto_props/client';

declare global {
  interface Window {
    chatbotLoaded?: (callback: () => void) => void;
    wonderchat?: {
      toggleChat: (show?: boolean) => void;
      changeChatbotId: (id: string) => void;
      identify: (params: Record<string, unknown>) => void;
      clearHistory: () => void;
      prefillQuestion: (question: string) => void;
    };
  }
}

let scriptLoaded = false;
let shouldOpenChat = false;
let chatReady = false;
let hiddenStyleEl: HTMLStyleElement | null = null;

function isWonderchatEnabled(): boolean {
  try {
    return getAutoProps('BulkDownloadFlags')?.wonderchatEnabled === true;
  } catch {
    return false;
  }
}

function injectHiddenStyle(): void {
  if (shouldOpenChat || hiddenStyleEl) return;
  hiddenStyleEl = document.createElement('style');
  hiddenStyleEl.textContent =
    '#wonderchat-wrapper { display: none !important; }';
  document.head.appendChild(hiddenStyleEl);
}

function removeHiddenStyle(): void {
  if (hiddenStyleEl) {
    hiddenStyleEl.remove();
    hiddenStyleEl = null;
  }
}

function openChatIfNeeded(): void {
  if (shouldOpenChat) {
    window.wonderchat?.toggleChat(true);
  }
}

/**
 * Dynamically loads the Wonderchat chatbot widget script. The widget is
 * hidden via an injected stylesheet so it loads silently in the background.
 * Safe to call multiple times — the script is only appended once.
 */
export function loadWonderchatScript(): void {
  if (scriptLoaded || !isWonderchatEnabled()) return;
  scriptLoaded = true;

  injectHiddenStyle();

  const script = document.createElement('script');
  script.src = 'https://app.wonderchat.io/scripts/wonderchat.js';
  script.setAttribute('data-name', 'wonderchat');
  script.setAttribute('data-address', 'app.wonderchat.io');
  script.setAttribute('data-id', 'cmkro2dk013z2nkkiditmd6e5');
  script.setAttribute('data-widget-size', 'normal');
  script.setAttribute('data-widget-button-size', 'normal');
  script.defer = true;

  script.addEventListener('load', () => {
    window.chatbotLoaded?.(() => {
      chatReady = true;
      openChatIfNeeded();
    });
  });

  document.body.appendChild(script);
}

/**
 * Reveals the Wonderchat widget and automatically opens the chat window.
 * Uses the client SDK's `chatbotLoaded` callback to handle the case where
 * the widget hasn't finished initializing yet.
 */
export function showWonderchatWidget(): void {
  shouldOpenChat = true;
  removeHiddenStyle();

  if (chatReady) {
    openChatIfNeeded();
  }
}
